import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Ticket, CalendarCheck, HourglassMedium, ArrowSquareOut, MapTrifold, X, Star, Bell, BellSlash } from "@phosphor-icons/react";
import api, { formatApiErrorDetail } from "../../lib/api";
import { useFavourites } from "../../components/FavouriteStar";
import { ReviewModal, Stars } from "../../components/ClassReview";

const when = (iso) => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso || "";
  return d.toLocaleString(undefined, { weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
};

function BizTag({ b }) {
  return (
    <span className="inline-flex items-center gap-1.5 tracker text-[9px] text-zinc-400">
      <span className="w-2 h-2 rotate-45" style={{ background: b.accent }} /> {b.brand_name}{b.city ? ` · ${b.city}` : ""}
    </span>
  );
}

function Favourites() {
  const { isMember, ids, toggle, reload } = useFavourites();
  const [favs, setFavs] = useState(null);
  const fetch = () => api.get("/member/favourites").then(({ data }) => setFavs(data.favourites)).catch(() => setFavs([]));
  useEffect(() => { if (isMember) fetch(); }, [isMember, ids]);
  if (!isMember || !favs) return null;
  const setAlerts = async (f, alerts) => {
    try { await api.patch(`/member/favourites/${f.trainer_id}`, { alerts }); toast.success(alerts ? "Alerts on" : "Alerts off"); fetch(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  return (
    <section className="space-y-3" data-testid="member-favourites">
      <h2 className="tracker text-zinc-500 text-[10px] flex items-center gap-2"><Star size={12} weight="fill" className="text-amber-400" /> FAVOURITES · {favs.length}</h2>
      {favs.length === 0 ? (
        <p className="text-sm text-zinc-500 border border-dashed border-white/10 px-4 py-3" data-testid="member-favourites-empty">Star a business on <Link to="/member/discover" className="text-brand underline">Discover</Link> or its schedule page to get an email when it adds classes or a spot opens up.</p>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {favs.map((f) => (
            <div key={f.trainer_id} className="card-flat p-4 flex flex-col gap-2" data-testid={`member-favourite-${f.trainer_id}`}>
              <div className="flex items-center justify-between gap-2">
                <BizTag b={f.business} />
                <div className="flex items-center gap-1">
                  <button onClick={() => setAlerts(f, !f.alerts)} className={`p-1 ${f.alerts ? "text-emerald-400" : "text-zinc-600"}`} title={f.alerts ? "Alerts on — click to mute" : "Alerts muted — click to turn on"} data-testid={`member-favourite-alerts-${f.trainer_id}`}>
                    {f.alerts ? <Bell size={14} weight="fill" /> : <BellSlash size={14} />}
                  </button>
                  <button onClick={() => toggle(f.trainer_id, f.business.brand_name).then(() => { reload(); fetch(); })} className="p-1 text-amber-400" title="Remove from favourites" data-testid={`member-favourite-remove-${f.trainer_id}`}><Star size={14} weight="fill" /></button>
                </div>
              </div>
              <div className="text-xs text-zinc-400">
                {f.next_class ? <>Next: <span className="text-zinc-200">{f.next_class.title}</span> · {when(f.next_class.start)}</> : "No upcoming classes yet"}
                {f.upcoming_count > 1 && <span className="tracker text-[9px] text-zinc-600"> · {f.upcoming_count} UPCOMING</span>}
              </div>
              {f.business.group_url && <a href={f.business.group_url} className="btn-ghost !py-1 !px-2 !text-[9px] self-start" data-testid={`member-favourite-book-${f.trainer_id}`}>See schedule</a>}
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

export default function MemberClasses() {
  const [data, setData] = useState(null);
  const [reviewing, setReviewing] = useState(null);
  const [params, setParams] = useSearchParams();
  const load = () => api.get("/member/overview").then(({ data }) => setData(data)).catch(() => setData({ upcoming: [], past: [], waitlist: [], credits: [], credits_total: 0 }));
  useEffect(() => { load(); }, []);
  useEffect(() => {
    const id = params.get("review");
    if (!id || !data) return;
    const b = data.past.find((p) => p.id === id);
    if (b) setReviewing({ ...b, initialRating: Number(params.get("rating")) || 0 });
    setParams({}, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const cancel = async (b) => {
    if (!window.confirm(`Cancel your spot in ${b.title}?`)) return;
    try { await api.post(`/client/group/bookings/${b.id}/cancel`); toast.success("Booking cancelled"); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const leave = async (w) => {
    try { await api.post(`/client/group/waitlist/${w.id}/leave`); toast.success("Left the waitlist"); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  if (!data) return <p className="tracker text-zinc-500">Loading…</p>;

  return (
    <div className="space-y-10" data-testid="member-classes">
      <div>
        <div className="tracker text-brand mb-3">[ My classes ]</div>
        <h1 className="font-display text-4xl sm:text-5xl">Your bookings, everywhere.</h1>
        <p className="text-zinc-500 mt-2">Every class, waitlist spot and credit across all the businesses you book with.</p>
      </div>

      <Favourites />

      {data.credits.length > 0 && (
        <section className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="member-credits">
          {data.credits.map((c) => (
            <div key={c.business.id} className="card-flat p-5 flex items-center gap-4" data-testid={`member-credits-${c.business.id}`}>
              <Ticket size={26} weight="duotone" className="text-emerald-400 shrink-0" />
              <div className="min-w-0">
                <div className="font-display text-2xl">{c.credits} credit{c.credits === 1 ? "" : "s"}</div>
                <BizTag b={c.business} />
                <div className="tracker text-[9px] text-zinc-600 mt-1">USED AUTOMATICALLY WHEN YOU BOOK</div>
              </div>
              {c.business.group_url && <a href={c.business.group_url} className="btn-ghost !py-1 !px-2 !text-[9px] ml-auto shrink-0" data-testid={`member-credits-book-${c.business.id}`}>Book</a>}
            </div>
          ))}
        </section>
      )}

      <section className="space-y-3">
        <h2 className="tracker text-zinc-500 text-[10px] flex items-center gap-2"><CalendarCheck size={12} /> UPCOMING · {data.upcoming.length}</h2>
        {data.upcoming.length === 0 ? (
          <div className="card-flat p-10 text-center" data-testid="member-upcoming-empty">
            <MapTrifold size={28} weight="duotone" className="text-brand mx-auto mb-3" />
            <p className="font-display text-xl">Nothing booked yet.</p>
            <p className="text-zinc-500 text-sm mt-1 mb-4">Find a studio or coach near you and grab a spot.</p>
            <Link to="/member/discover" className="btn-primary" data-testid="member-go-discover">Discover businesses</Link>
          </div>
        ) : (
          <div className="divide-y divide-white/5 border border-white/10" data-testid="member-upcoming">
            {data.upcoming.map((b) => (
              <div key={b.id} className="px-4 py-3 flex items-center justify-between gap-3" data-testid={`member-booking-${b.id}`}>
                <div className="min-w-0">
                  <div className="font-display text-lg truncate">{b.title}</div>
                  <div className="text-xs text-zinc-400">{when(b.start)}</div>
                  <div className="mt-1 flex items-center gap-3">
                    <BizTag b={b.business} />
                    <span className={`tracker text-[9px] ${b.status === "paid" ? "text-emerald-400" : "text-amber-400"}`}>{b.status === "paid" ? (b.paid_via === "pack" ? "PAID · CREDIT" : "PAID") : "AWAITING PAYMENT"}</span>
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {b.pay_url && <a href={b.pay_url} className="btn-primary !py-1 !px-2 !text-[9px]" data-testid={`member-booking-pay-${b.id}`}>Pay now <ArrowSquareOut size={10} /></a>}
                  <button onClick={() => cancel(b)} className="btn-ghost !py-1 !px-2 !text-[9px]" data-testid={`member-booking-cancel-${b.id}`}><X size={10} /> Cancel</button>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {data.waitlist.length > 0 && (
        <section className="space-y-3" data-testid="member-waitlist">
          <h2 className="tracker text-zinc-500 text-[10px] flex items-center gap-2"><HourglassMedium size={12} /> WAITLISTS · {data.waitlist.length}</h2>
          <div className="divide-y divide-white/5 border border-white/10">
            {data.waitlist.map((w) => (
              <div key={w.id} className="px-4 py-3 flex items-center justify-between gap-3" data-testid={`member-waitlist-${w.id}`}>
                <div className="min-w-0">
                  <div className="font-display text-lg truncate">{w.title}</div>
                  <div className="text-xs text-zinc-400">{when(w.start)} · #{w.position} in line</div>
                  <div className="mt-1"><BizTag b={w.business} /></div>
                </div>
                <button onClick={() => leave(w)} className="btn-ghost !py-1 !px-2 !text-[9px] shrink-0" data-testid={`member-waitlist-leave-${w.id}`}>Leave</button>
              </div>
            ))}
          </div>
        </section>
      )}

      {reviewing && <ReviewModal booking={reviewing} initialRating={reviewing.initialRating} onClose={() => setReviewing(null)} onSaved={load} />}

      {data.past.length > 0 && (
        <section className="space-y-3" data-testid="member-past">
          <h2 className="tracker text-zinc-500 text-[10px]">PAST · {data.past.length}</h2>
          <div className="divide-y divide-white/5 border border-white/10">
            {data.past.map((b) => (
              <div key={b.id} className="px-4 py-2.5 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-sm truncate">{b.title}</div>
                  <div className="text-[11px] text-zinc-500">{when(b.start)} · <BizTag b={b.business} /></div>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  {b.attendance === "no_show" && <span className="tracker text-[9px] text-zinc-500">NO-SHOW</span>}
                  {b.review ? (
                    <button onClick={() => setReviewing(b)} className="inline-flex items-center gap-2" title="Edit your review" data-testid={`member-review-edit-${b.id}`}><Stars value={b.review.rating} /></button>
                  ) : b.can_review ? (
                    <button onClick={() => setReviewing(b)} className="btn-ghost !py-1 !px-2 !text-[9px]" data-testid={`member-review-btn-${b.id}`}><Star size={10} /> Rate this class</button>
                  ) : null}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
